import CommonTextInput from '@atoms/CommonTextInput';
import RightArrow from '@atoms/RightArrow';
import { Button, Text, View } from '@components/primitives';
import BottomSheet from '@molecules/LoginInfoSheet';
import navigationConstants from '@navigation/navigationConstants';
import { CommonActions, NavigationProp } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { COMMON_CONSTANTS } from '@root/constants';
import colors from '@theme/colors';
import { verticalScale } from '@utils/normalize';
import { showSuccessToast, showWarningToast } from '@utils/toastManager';
import { removeNonNumeric, resetStackToCustomScreen } from '@utils/utils';
import React from 'react';
import { Linking, TouchableOpacity } from 'react-native';
import {
  logAccountNumberClicked,
  logAccountNumberFilled,
  logBankNameClicked,
  logBeneficiaryNameClicked,
  logBeneficiaryNameFilled,
  logNickNameClicked,
  logNickNameFilled,
  logThreeTry,
  logThreeTryVerify,
} from './analyticsBankScreen';
import styles from './styles';

interface Values {
  accountName?: string;
  accountNumber?: string;
  selectedBank?: {
    id: number;
    bankName?: string;
  };
}

interface Errors {
  accountName?: string;
  accountNumber?: string;
}

interface Params {
  fromTransfer?: boolean;
  [key: string]: any;
}

export function disableButtonForValues(values: Values, errors: Errors): boolean {
  let result = false;
  if (values?.accountName?.trim() === '' || (errors?.accountName && errors.accountName.length > 0)) {
    result = true;
  }
  if (values?.accountNumber?.trim() === '' || (errors?.accountNumber && errors.accountNumber.length > 0)) {
    result = true;
  }
  if (values && values?.selectedBank?.id === -1) {
    result = true;
  }
  return result;
}

export function shouldShowBankNameOrPlaceholder(
  selectedBank: Values['selectedBank'] | null,
  t: (key: string) => string,
): string {
  return selectedBank === null ? t('addBank.bankTitlePlaceholder') : selectedBank.bankName || '';
}

interface ApiData {
  status?: 'PENDING' | 'APPROVED' | string;
  [key: string]: any;
}

export function shouldRenderSuccessfulBankAdd(
  isSuccess: boolean,
  data: ApiData | undefined,
  toast: any,
  t: (key: string) => string,
  navigation: NavigationProp<any>,
  params: Params,
): void {
  if (isSuccess && data) {
    if (data.status === 'PENDING') {
      showWarningToast(toast, t('addBank.addPending'), 'addBankPending');
    }

    if (data.status === 'APPROVED') {
      showSuccessToast(toast, t('addBank.addApproved'), 'addApproved');
    }

    const { routes } = navigation.getState();
    const enrolledBankExists = routes.filter(item => item.name === navigationConstants.BANK_LIST);

    if (enrolledBankExists.length > 0) {
      resetStackToCustomScreen(navigation, params, navigationConstants.BANK_LIST);
      return;
    }

    if (params?.fromTransfer) {
      navigation.dispatch(
        CommonActions.reset({
          index: 2,
          routes: [
            routes[0],
            { name: navigationConstants.GG_BANK_TRANSFER, params },
            { name: navigationConstants.BANK_LIST, params: { fromTransfer: false } },
          ],
        }),
      );
      return;
    }

    navigation.dispatch(
      CommonActions.reset({
        index: 2,
        routes: [routes[0], { name: navigationConstants.BANK_LIST, params: { fromTransfer: false } }],
      }),
    );
  }
}
interface SelectedBank {
  id: number;
  bankName?: string;
}

interface FormValues {
  accountNumber: string;
  accountName: string;
  accountNickName: string;
  selectedBank: SelectedBank;
}

interface FormErrors {
  accountNumber?: string;
  accountName?: string;
  accountNickName?: string;
}

interface ModalData {
  visible: boolean;
  errorCode?: string;
  [key: string]: any;
}

interface RenderContentParams {
  handleChange: (field: string) => (value: string) => void;
  handleSubmit: () => void;
  values: FormValues;
  errors: FormErrors;
}

export function renderFormContentFunction(
  t: (key: string) => string,
  openBankList: () => void,
  selectedBank: SelectedBank | null,
  setInfoModal: (modal: ModalData) => void,
  infoModal: ModalData,
  addBankMutate: (data: Record<string, any>) => void,
  onPressSecondaryHandler: () => void,
  onCloseHandler: (values: FormValues) => void,
  source: string,
) {
  const RenderFormContent: React.FC<RenderContentParams> = ({ handleChange, values, errors, handleSubmit }) => (
    <View style={[appStyles.marginHorizontal(16), appStyles.marginVertical(16)]}>
      {/* Bank Selection */}
      <View>
        <Text variant="sm-normal" color={colors.neutral.darkMist}>
          {t('addBank.bankNameTitle')}
        </Text>
        <TouchableOpacity testID="btnSelectBank" style={styles.bankSelectButton} onPress={openBankList}>
          <Text variant="md-normal" color={selectedBank ? colors.neutral.charcoal : colors.neutral.darkMist} flex={1}>
            {shouldShowBankNameOrPlaceholder(selectedBank, t)}
          </Text>
          <RightArrow />
        </TouchableOpacity>
      </View>

      {/* Account Number */}
      <View style={appStyles.marginTop(16)}>
        <Text variant="sm-normal" color={colors.neutral.darkMist}>
          {t('addBank.bankAccontNumTitle')}
        </Text>
        <CommonTextInput
          testID="txtInputAccountNumber"
          onChangeText={val => {
            const accountNumber = removeNonNumeric(val);
            handleChange('accountNumber')(accountNumber);
          }}
          placeholderTextColor={colors.neutral.darkMist}
          value={values.accountNumber}
          style={styles.inputStyles}
          placeholder={t('addBank.bankNameTitlePlaceholder')}
          keyboardType="numeric"
          amplitudeCallBackOnFocus={logAccountNumberClicked(source)}
          amplitudeCallBackOnBlur={logAccountNumberFilled(source)}
        />
        {errors?.accountNumber ? (
          <Text variant="sm-normal" color={colors.error.rose}>
            {errors.accountNumber}
          </Text>
        ) : null}
      </View>

      {/* Account Name */}
      <View style={appStyles.marginTop(16)}>
        <Text color={colors.neutral.darkMist}>{t('addBank.bankAccountName')}</Text>
        <CommonTextInput
          testID="txtInputAccountName"
          onChangeText={handleChange('accountName')}
          value={values.accountName}
          style={styles.inputStyles}
          placeholderTextColor={colors.neutral.darkMist}
          placeholder={t('addBank.bankAccountNamePlaceholder')}
          amplitudeCallBackOnFocus={logBeneficiaryNameClicked(source)}
          amplitudeCallBackOnBlur={logBeneficiaryNameFilled(source)}
        />
        {values.accountName && errors?.accountName ? (
          <Text variant="sm-normal" color={colors.error.rose}>
            {errors.accountName}
          </Text>
        ) : null}
      </View>

      {/* Account NickName */}
      <View style={appStyles.marginTop(16)}>
        <Text color={colors.neutral.darkMist}>{t('addBank.bankAccountNickName')}</Text>
        <CommonTextInput
          testID="txtInputAccountNickName"
          onChangeText={handleChange('accountNickName')}
          value={values.accountNickName}
          style={styles.inputStyles}
          placeholderTextColor={colors.neutral.darkMist}
          placeholder={t('addBank.bankAccountNickNamePlaceholder')}
          amplitudeCallBackOnFocus={logNickNameClicked(source)}
          amplitudeCallBackOnBlur={logNickNameFilled(source)}
        />
      </View>

      {/* Submit */}
      <Button
        testID="btnAddBankSubmitForm"
        isDisabled={disableButtonForValues(values, errors)}
        style={[appStyles.marginVertical(16), { paddingVertical: verticalScale(8) }]}
        onPress={handleSubmit}>
        {t('addBank.bankAccountAdd')}
      </Button>

      {/* Modal */}
      <BottomSheet
        onPressPrimary={() => {
          setInfoModal({ ...infoModal, visible: false });
          if (infoModal.errorCode === 'THREE_RETRY_VERIFICATION') {
            logThreeTry();
            addBankMutate({
              accountNumber: values.accountNumber,
              bankId: values.selectedBank.id,
              beneficiaryName: values.accountName,
              nickName: values.accountNickName,
            });
          }
        }}
        loginInfo={infoModal}
        onPressSecondary={onPressSecondaryHandler}
        onClose={() => onCloseHandler(values)}
      />
    </View>
  );

  return RenderFormContent;
}

interface BankHandlerParams {
  setInfoModal: React.Dispatch<React.SetStateAction<ModalData>>;
  infoModal: ModalData;
  navigation: NavigationProp<any>;
  setSelectedBank: (bank: any) => void;
  source: string;
}

interface BankHandlers {
  openBankList: () => void;
  onPressSecondaryHandler: () => void;
}

export function addBankHandlers(bankHandlerParams: BankHandlerParams): BankHandlers {
  const { setInfoModal, infoModal, navigation, setSelectedBank, source } = bankHandlerParams;

  const resetPinHandler = (): void => {
    Linking.openURL(COMMON_CONSTANTS.support);
  };

  const onPressSecondaryHandler = (): void => {
    setInfoModal(prevState => ({ ...prevState, visible: false }));
    if (infoModal.errorCode === 'THREE_RETRY_VERIFICATION') {
      resetPinHandler();
      logThreeTryVerify();
    }
  };

  const openBankList = (): void => {
    logBankNameClicked(source);
    navigation.navigate(navigationConstants.GG_ALL_BANKS, { setSelectedBank, source });
  };

  return { openBankList, onPressSecondaryHandler };
}
